"use client";

// Visible breadcrumb trail, derived from the URL so every page gets one
// without each page having to describe itself.
//
// Rendered inside PageHeader (which most interior pages use) and directly on
// the handful of pages that build their own hero. The homepage has no trail.
//
// Segments are humanised from the slug; pages whose last segment is an opaque
// slug (a centre, a blog post, an article) pass `labels` to name it properly:
//   <Breadcrumbs labels={{ "ts-towers-whitefield": "Table Space Towers Whitefield" }} />
//
// This is the visible trail only. BreadcrumbList structured data is emitted
// separately by the pages that carry it.
import Link from "next/link";
import { usePathname } from "next/navigation";

// Words the generic title-casing would otherwise mangle.
const FIXED: Record<string, string> = {
  faqs: "FAQs",
  gcc: "GCC",
  desyn: "DESYN",
  ipo: "IPO",
  sebi: "SEBI",
  aoa: "AOA",
  moa: "MOA",
  hitec: "HITEC",
  cbd: "CBD",
  ncr: "NCR",
  us: "US",
  ai: "AI",
  and: "and",
  in: "in",
  of: "of",
  the: "the",
  to: "to",
  a: "a",
  v2: "v2",
};

// Whole segments with an established name that is not a title-cased slug.
const SEGMENT_LABELS: Record<string, string> = {
  "global-capability-center": "Global Capability Centre",
  "global-capability-center-v2": "Global Capability Centre (v2)",
  "investor-relations": "Investor Relations",
  "table-space-policies": "Policies",
  "terms-and-conditions": "Terms & Conditions",
  "privacy-policy": "Privacy Policy",
  "amit-dedication-page": "Amit Banerji",
  "managed-office-space": "Managed Office Space",
  "private-office-space": "Private Office Space",
  "office-space-in-india": "Office Space in India",
  "workspace-solutions-in-india": "Workspace Solutions in India",
  "enterprise-services": "Enterprise Services",
  "custom-built": "Custom Built",
  "meeting-rooms": "Meeting Rooms",
  suites: "Suites",
  solutions: "Solutions",
  locations: "Locations",
  technology: "Technology",
  sustainability: "Sustainability",
  media: "Media",
  blog: "Blog",
  careers: "Careers",
  contact: "Contact",
  about: "About",
  legal: "Legal",
  sitemap: "Sitemap",
  enterprise: "Enterprise",
  events: "Events",
  shoots: "Shoots",
};

function humanise(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  return segment
    .split("-")
    .map((w) => FIXED[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** The legacy city URLs (/office-space-in-bengaluru/whitefield) read better as
 *  Locations › Bengaluru › Whitefield than as their raw slugs. */
function expand(segments: string[]): { href: string; label: string }[] {
  const city = segments[0]?.match(/^office-space-in-(.+)$/)?.[1];
  if (city && city !== "india") {
    const rest = segments.slice(1);
    return [
      { href: "/locations", label: "Locations" },
      { href: `/locations/${city}`, label: humanise(city) },
      ...rest.map((s, i) => ({
        href: `/${segments.slice(0, i + 2).join("/")}`,
        label: humanise(s),
      })),
    ];
  }
  return segments.map((s, i) => ({
    href: `/${segments.slice(0, i + 1).join("/")}`,
    label: humanise(s),
  }));
}

export function Breadcrumbs({
  tone = "dark",
  labels = {},
  className = "",
}: {
  /** "dark" sits over the image hero (light text); "light" over a pale page. */
  tone?: "dark" | "light";
  /** Override the label for a given path segment — used for opaque slugs. */
  labels?: Record<string, string>;
  className?: string;
}) {
  const pathname = usePathname() || "/";
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null; // the homepage is the root

  const crumbs = expand(segments).map((c) => ({
    ...c,
    label: labels[c.href.split("/").filter(Boolean).pop() ?? ""] ?? c.label,
  }));

  const base = tone === "dark" ? "text-white/70" : "text-brand-muted";
  const link = tone === "dark" ? "hover:text-white" : "hover:text-brand-accent-dark";
  const current = tone === "dark" ? "text-white" : "text-brand-ink";

  return (
    <nav aria-label="Breadcrumb" className={`${base} ${className}`}>
      <ol className="mono-meta flex flex-wrap items-center gap-x-2 gap-y-1 uppercase">
        <li className="flex items-center gap-x-2">
          <Link href="/" className={`transition-colors ${link}`}>
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <li key={c.href} className="flex items-center gap-x-2">
              <span aria-hidden className="opacity-50">/</span>
              {isLast ? (
                <span aria-current="page" className={current}>
                  {c.label}
                </span>
              ) : (
                <Link href={c.href} className={`transition-colors ${link}`}>
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
